/**
 * Centralized date formatting utilities
 *
 * This module provides consistent date formatting functions used across the application.
 * All date formatting should go through these utilities for consistency.
 */

/**
 * Proto date type (from generated protobuf messages)
 */
export type ProtoDate = {
  year?: number
  month?: number
  day?: number
}

/**
 * Format a proto date object to ISO 8601 date string (YYYY-MM-DD).
 *
 * Used in: PassportCard, ScheduleContentPanel
 *
 * @example
 * ```ts
 * formatProtoDate({ year: 2025, month: 1, day: 15 }) // "2025-01-15"
 * formatProtoDate({ year: 2025 }) // "2025-01-01"
 * formatProtoDate(undefined) // null
 * ```
 */
export function formatProtoDate(date: ProtoDate | undefined): string | null {
  if (!date?.year) return null
  const month = String(date.month || 1).padStart(2, '0')
  const day = String(date.day || 1).padStart(2, '0')
  return `${date.year}-${month}-${day}`
}

/**
 * Calculate duration in days between two proto dates.
 *
 * Used in: PassportCard, TripListOverlay
 *
 * @example
 * ```ts
 * calculateDuration(
 *   { year: 2025, month: 1, day: 15 },
 *   { year: 2025, month: 1, day: 20 }
 * ) // 5
 * ```
 */
export function calculateDuration(
  departureDate: ProtoDate | undefined,
  returnDate: ProtoDate | undefined
): number | null {
  if (!departureDate?.year || !returnDate?.year) return null

  const departure = new Date(
    departureDate.year,
    (departureDate.month || 1) - 1,
    departureDate.day || 1
  )
  const returnDay = new Date(
    returnDate.year,
    (returnDate.month || 1) - 1,
    returnDate.day || 1
  )

  const diffTime = Math.abs(returnDay.getTime() - departure.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return diffDays
}

/**
 * Format an ISO date string to short month and day format.
 *
 * Used in: TransportationToolRenderer
 *
 * @example
 * ```ts
 * formatShortDate("2025-01-15") // "Jan 15"
 * ```
 */
export function formatShortDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return dateString
  }
}

/**
 * Format a date range from ISO strings.
 * Shows abbreviated format like "Jan 15-18" if same month, or "Jan 15 - Feb 2" if different months.
 *
 * Used in: HotelToolRenderer
 *
 * @example
 * ```ts
 * formatDateRange("2025-01-15", "2025-01-18") // "Jan 15-18"
 * formatDateRange("2025-01-30", "2025-02-05") // "Jan 30 - Feb 5"
 * ```
 */
export function formatDateRange(checkIn: string, checkOut: string): string {
  try {
    const inDate = new Date(checkIn)
    const outDate = new Date(checkOut)

    const inMonth = inDate.toLocaleDateString('en-US', { month: 'short' })
    const inDay = inDate.getDate()
    const outDay = outDate.getDate()

    // If same month, show "Jan 15-18"
    if (inDate.getMonth() === outDate.getMonth()) {
      return `${inMonth} ${inDay}-${outDay}`
    }

    // Different months, show "Jan 15 - Feb 2"
    const outMonth = outDate.toLocaleDateString('en-US', { month: 'short' })
    return `${inMonth} ${inDay} - ${outMonth} ${outDay}`
  } catch {
    return `${checkIn} - ${checkOut}`
  }
}

/**
 * Format a date to month and year (e.g., "Dec 2025").
 *
 * Used in: ScheduleCalendarWidget
 *
 * @example
 * ```ts
 * formatMonthYear(new Date('2025-12-15')) // "Dec 2025"
 * ```
 */
export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/**
 * Convert proto date to JavaScript Date object.
 * Defaults to day 1 and month 1 if not provided.
 *
 * @example
 * ```ts
 * protoDateToDate({ year: 2025, month: 1, day: 15 }) // Date object for 2025-01-15
 * ```
 */
export function protoDateToDate(date: ProtoDate | undefined): Date | null {
  if (!date?.year) return null
  return new Date(
    date.year,
    (date.month || 1) - 1,
    date.day || 1
  )
}

/**
 * Convert JavaScript Date to proto date object.
 *
 * @example
 * ```ts
 * dateToProtoDate(new Date('2025-01-15')) // { year: 2025, month: 1, day: 15 }
 * ```
 */
export function dateToProtoDate(date: Date): ProtoDate {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  }
}

/**
 * Proto TimeOfDay type (from generated protobuf messages)
 */
export type ProtoTimeOfDay = {
  hour?: number
  minute?: number
  second?: number
  nanos?: number
}

/**
 * Proto Duration type (from generated protobuf messages)
 */
export type ProtoDuration = {
  seconds?: bigint | string
  nanos?: number
}

/**
 * Format a proto TimeOfDay object to 12-hour time string.
 *
 * Used in: EventDetailModal, ScheduleCard, ScheduleDrawer
 *
 * @param startTime - TimeOfDay proto object
 * @returns Formatted time string (e.g., "2:30 PM") or "Time TBD" if no time provided
 *
 * @example
 * ```ts
 * formatTime({ hour: 14, minute: 30 }) // "2:30 PM"
 * formatTime({ hour: 0, minute: 15 }) // "12:15 AM"
 * formatTime(undefined) // "Time TBD"
 * ```
 */
export function formatTime(startTime?: ProtoTimeOfDay): string {
  if (!startTime?.hour && startTime?.hour !== 0) return "Time TBD";
  const hour = startTime.hour || 0;
  const minute = startTime.minute || 0;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
}

/**
 * Format a proto Duration object (in seconds) to human-readable string.
 *
 * Note: For millisecond-based durations, use formatDurationFromMilliseconds from eventUtils.ts
 * Note: For minute-based durations, see transportation/types.ts
 *
 * Used in: EventDetailModal, ScheduleCard
 *
 * @param duration - Duration proto object with seconds field
 * @returns Formatted duration string (e.g., "2h 30m", "1h", "45m") or empty string if no duration
 *
 * @example
 * ```ts
 * formatDurationFromSeconds({ seconds: 7200n }) // "2h"
 * formatDurationFromSeconds({ seconds: "3900" }) // "1h 5m"
 * formatDurationFromSeconds({ seconds: 45n }) // ""
 * formatDurationFromSeconds(undefined) // ""
 * ```
 */
export function formatDurationFromSeconds(duration?: ProtoDuration): string {
  if (!duration?.seconds) return "";
  const seconds = Number(duration.seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  }
  return "";
}
